/**
 * KÖRVONAL — cart "magánszemély vagy cég" selector + adószám capture.
 *
 * Ported from the Körvonal 2024 theme's assets/tax-validator.js, keeping its
 * proven storage key (`taxNumber` cart attribute) and validation rule.
 *
 * ONE DELIBERATE BEHAVIOUR CHANGE: the original REQUIRED a choice and blocked
 * checkout until one was made — friction on every order, when ~95% of them are
 * private buyers. Here the choice is optional; the script only intervenes once
 * a buyer has actively selected "Cég".
 *
 * The three failure modes of the original are fixed:
 *
 *   1. The original bound listeners directly to the buttons on DOMContentLoaded.
 *      component-cart.js replaces the whole #AjaxCartSubtotal innerHTML on every
 *      cart change, so those listeners died on the first quantity tweak and
 *      checkout silently stopped validating. Everything here is DELEGATED from
 *      document, which survives any re-render.
 *   2. The original ran querySelector("#ceg").checked = ... unguarded, so on any
 *      page where the block was absent it threw and took the rest of the handler
 *      with it. Every lookup here is null-guarded and the script no-ops when the
 *      block is not on the page.
 *   3. The original regex was unanchored (.match without ^$), so "x12345678-2-42y"
 *      passed validation. Anchored here.
 *
 * The snippet renders the current state from cart.attributes.taxNumber, so a
 * typed number survives a re-render on its own; only the "Magánszemély" choice
 * (which deliberately stores nothing on the order) is restored from
 * sessionStorage.
 */
(function () {
  "use strict";

  if (window.__korvonalTaxInit) return;
  window.__korvonalTaxInit = true;

  // Domestic Hungarian tax number: 8 digits - VAT-type digit - county code.
  // Same rule the 2024 theme used, anchored.
  var TAX_RE = /^\d{8}-[1-5]-(?:0[2-9]|[13][0-9]|2[02-9]|4[0-4]|51)$/;
  var CHOICE_KEY = "korvonal_tax_choice";

  function root()      { return document.querySelector("[data-korvonal-tax]"); }
  function el(sel, ctx) { return (ctx || document).querySelector(sel); }

  function setError(msg) {
    var e = el("[data-korvonal-tax-error]");
    if (e) e.textContent = msg || "";
  }

  /** Persist to the cart. "" clears it (private buyer). */
  function writeTaxNumber(value) {
    return fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: { taxNumber: value } })
    }).catch(function () { /* network hiccup must not trap the buyer in the cart */ });
  }

  function showField(show) {
    var f = el("[data-korvonal-tax-field]");
    if (f) f.hidden = !show;
  }

  /** Re-apply the private-buyer choice after the drawer re-renders. */
  function restoreChoice() {
    var r = root();
    if (!r) return;
    var person  = el("[data-korvonal-tax-person]", r);
    var company = el("[data-korvonal-tax-company]", r);
    if (!person || !company) return;
    // A company number renders as checked from Liquid — never override that.
    if (company.checked) { showField(true); return; }
    try {
      if (sessionStorage.getItem(CHOICE_KEY) === "person") person.checked = true;
    } catch (e) { /* private mode */ }
  }

  function remember(choice) {
    try { sessionStorage.setItem(CHOICE_KEY, choice); } catch (e) { /* private mode */ }
  }

  // ── Choice ────────────────────────────────────────────────────────────────
  // Delegated on `change` (not a click on the row, as the original did — that
  // fired twice when the row's own label already toggled the radio).
  document.addEventListener("change", function (e) {
    var t = e.target;
    if (!t || !t.closest || !t.closest("[data-korvonal-tax]")) return;

    if (t.hasAttribute("data-korvonal-tax-person")) {
      setError("");
      showField(false);
      remember("person");
      var input = el("[data-korvonal-tax-input]");
      if (input) input.value = "";
      writeTaxNumber("");
    } else if (t.hasAttribute("data-korvonal-tax-company")) {
      setError("");
      showField(true);
      remember("company");
      var i = el("[data-korvonal-tax-input]");
      if (i) i.focus();
    }
  });

  // Clear a stale error as soon as the buyer edits the number.
  document.addEventListener("input", function (e) {
    if (e.target && e.target.hasAttribute && e.target.hasAttribute("data-korvonal-tax-input")) setError("");
  });

  // ── Checkout ──────────────────────────────────────────────────────────────
  // The choice is OPTIONAL: picking nothing is a valid way through, so a buyer
  // who ignores the block reaches checkout exactly as the theme intends — no
  // preventDefault, no interception, nothing to go wrong for the ~95% who are
  // private buyers.
  //
  // We only step in once someone has actively claimed to be a company, and then
  // only to make sure the number is well-formed and actually persisted before
  // the browser leaves the page. Capture phase + stopImmediatePropagation so the
  // theme's own handler cannot navigate out from under the pending write.
  document.addEventListener("click", function (e) {
    var btn = e.target && e.target.closest ? e.target.closest("#CheckOut, #CheckOutAlternate") : null;
    if (!btn) return;

    var r = root();
    if (!r || r.hidden) return;           // feature off, non-HU, or preview-not-opted-in
    if (r.dataset.passed === "1") return; // already validated — let the real click through

    var company = el("[data-korvonal-tax-company]", r);
    if (!company || !company.checked) return; // no company claim → normal checkout

    e.preventDefault();
    e.stopImmediatePropagation();

    var input = el("[data-korvonal-tax-input]", r);
    var value = ((input && input.value) || "").replace(/\s+/g, "");
    if (!TAX_RE.test(value)) {
      setError("Kérlek ellenőrizd az adószám formátumát! Például: 12345678-2-42");
      if (input) input.focus();
      return;
    }

    setError("");
    r.dataset.passed = "1";
    writeTaxNumber(value).then(function () {
      window.location.href = "/checkout";
    });
  }, true);

  // ── Re-render + preview ───────────────────────────────────────────────────
  function boot() {
    // Preview opt-in is applied as a class on <html> so the CSS keeps the block
    // visible through every subsequent innerHTML replacement, with no JS re-run.
    try {
      var fresh = new URLSearchParams(location.search).get("adoszam");
      if (fresh === "off") sessionStorage.removeItem("korvonal_tax_preview");
      else if (fresh) sessionStorage.setItem("korvonal_tax_preview", "1");
      if (sessionStorage.getItem("korvonal_tax_preview") === "1") {
        document.documentElement.classList.add("korvonal-tax-preview");
        var r0 = root();
        if (r0) r0.hidden = false;
      }
    } catch (e) { /* private mode */ }

    restoreChoice();

    var host = document.getElementById("AjaxCartSubtotal");
    if (host && window.MutationObserver) {
      new MutationObserver(function () {
        var r = root();
        if (r && document.documentElement.classList.contains("korvonal-tax-preview")) r.hidden = false;
        restoreChoice();
      }).observe(host, { childList: true, subtree: true });
    }
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
